import { z } from "zod";

const MIN_SUM_ASSURED = 2500000;
const MIN_LIFE_AGE = 18;

const toNumeric = (value: string) =>
  Number(
    String(value || "")
      .replace(/,/g, "")
      .trim()
  );

const getAgeInYears = (dob: string) => {
  const birthDate = new Date(dob);
  if (Number.isNaN(birthDate.getTime())) {
    return null;
  }

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }

  return age;
};

export const lifePlanDetailsSchema = z
  .object({
    lifeAssuredDob: z.string().min(1, "Life Assured Date of Birth is required"),
    lifeAssuredGender: z.enum(["male", "female"]),
    smoker: z.enum(["yes", "no"]),
    planOption: z.string().min(1, "Select Plan Option is required"),
    sumAssured: z.string().min(1, "Sum Assured is required"),
    policyTerm: z.string().min(1, "Policy Term is required"),
    premiumPaymentTerm: z.string().min(1, "Premium Payment Term is required"),
    premiumPaymentFrequency: z.string().min(1, "Premium Payment Frequency is required"),
    category: z.string().min(1, "Category is required"),
    payoutType: z.string().min(1, "Payout Type is required"),
    payoutTerm: z.string().optional().default(""),
    lumpsumPercentage: z.string().optional().default(""),
    accidentalDeathSumAssured: z.string().optional().default(""),
  })
  .superRefine((values, context) => {
    const sumAssured = toNumeric(values.sumAssured);
    if (Number.isNaN(sumAssured) || sumAssured < MIN_SUM_ASSURED) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sumAssured"],
        message: "Sum Assured must be at least 25,00,000",
      });
    }

    const age = getAgeInYears(values.lifeAssuredDob);
    if (age !== null && age < MIN_LIFE_AGE) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["lifeAssuredDob"],
        message: "Age must be 18 years or above",
      });
    }

    if (
      values.planOption === "extra_life_option" &&
      !(values.accidentalDeathSumAssured ?? "").trim()
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["accidentalDeathSumAssured"],
        message: "Accidental Death Sum Assured is required",
      });
    }

    if (
      ["fixed_income", "mixed"].includes(values.payoutType) &&
      !(values.payoutTerm ?? "").trim()
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["payoutTerm"],
        message: "Payout Term is required",
      });
    }

    if (values.payoutType === "mixed") {
      const percentage = String(values.lumpsumPercentage ?? "").trim();
      if (!percentage) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["lumpsumPercentage"],
          message: "Lumpsum Percentage is required",
        });
      } else {
        const numericValue = Number(percentage.replace(/,/g, ""));
        if (Number.isNaN(numericValue) || numericValue <= 0 || numericValue > 100) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["lumpsumPercentage"],
            message: "Lumpsum Percentage must be between 1 and 100",
          });
        }
      }
    }
  });
